import { TrashIcon } from "@heroicons/react/24/solid";
import classNames from "classnames";
import { useEffect, useState } from "react";
import { Accordion, useAccordionButton } from "react-bootstrap";
import { useFieldArray, useFormContext } from "react-hook-form";
import { IconButton } from "stories";
import { flatten } from "utils/objectUtilities";
import { FormRenderer } from ".";

const GroupHeader = ({
  eventKey,
  collapsable,
  itemPrefix,
  index,
  remove,
  isOpen,
}) => {
  const decoratedOnClick = useAccordionButton(eventKey);

  return (
    <div
      onClick={decoratedOnClick}
      className={classNames(
        { "cursor-pointer": collapsable },
        "px-4 py-3 section-title fw-bold d-flex align-items-center justify-content-between w-100"
      )}
    >
      <span>
        {itemPrefix} {index + 1}
      </span>
      <div className="d-flex align-items-center">
        <span>
          <IconButton onClick={remove.bind(null, index)}>
            <TrashIcon className="icon-md text-red" />
          </IconButton>
        </span>
        {collapsable && (
          <IconButton className={"ml-1"}>
            <div
              className={classNames(`toggle-icon icon-down-dir`, {
                "rotate-0": isOpen,
                "rotate-180": !isOpen,
              })}
            />
          </IconButton>
        )}
      </div>
    </div>
  );
};

export const GroupRenderer = ({
  parentFieldName,
  title,
  itemPrefix,
  addButtonText,
  fields,
  getModelItem,
  collapsable = true,
}) => {
  const {
    formState: { errors },
    ...form
  } = useFormContext();
  const {
    fields: items,
    append,
    remove,
  } = useFieldArray({
    control: form.control,
    name: parentFieldName,
    keyName: "key",
  });
  const handleAdd = () => {
    append(getModelItem());
  };
  const [openGroups, setOpenGroups] = useState({});
  useEffect(() => {
    for (const item of items) {
      if (typeof openGroups[item.key] === "undefined")
        openGroups[item.key] = true;
    }
    setOpenGroups({ ...openGroups });
  }, [items]); // eslint-disable-line

  const handleOnSelect = (openKeys) => {
    for (const itemKey of Object.keys(openGroups)) {
      if (openKeys.findIndex((i) => i === itemKey) === -1) {
        openGroups[itemKey] = false;
      } else {
        openGroups[itemKey] = true;
      }
    }
    setOpenGroups({ ...openGroups });
  };

  useEffect(() => {
    const flatErrors = flatten(errors);
    let openKeys = openGroups;
    let somethingChanges = false;
    for (const error of Object.keys(flatErrors)) {
      // if one of the children has error
      if (error.includes(parentFieldName + "[") && error.endsWith(".message")) {
        const i = error
          .replace(
            error.substring(
              0,
              error.indexOf(parentFieldName + "[") +
                (parentFieldName + "[").length
            ),
            ""
          )
          .charAt(0);
        if (!isNaN(parseInt(i))) {
          somethingChanges = true;
          openKeys[items[i].key] = true;
        }
      }
    }
    if (somethingChanges) {
      setTimeout(() => {
        setOpenGroups({ ...openKeys });
      }, 400);
    }
  }, [errors]); // eslint-disable-line

  const activeKeys = Object.keys(openGroups).filter((key) => openGroups[key]);
  return (
    <div className="mb-5">
      <div className="section-title fw-bold">{title}</div>
      <Accordion alwaysOpen onSelect={handleOnSelect} activeKey={activeKeys}>
        <div className="mt-3">
          {items.map((_, index) => {
            const namePrefix = `${parentFieldName}[${index}]`;
            return (
              <div
                className={classNames("border-gray", {
                  "border-top-0": index !== 0,
                })}
                key={_.key}
              >
                <GroupHeader
                  eventKey={_.key}
                  collapsable={collapsable}
                  itemPrefix={itemPrefix}
                  index={index}
                  remove={remove}
                  namePrefix={namePrefix}
                  isOpen={activeKeys.findIndex((key) => key === _.key) !== -1}
                />
                <Accordion.Collapse eventKey={_.key}>
                  <div className={"px-4 transition-all py-3"}>
                    <FormRenderer
                      fields={fields}
                      parentFieldName={namePrefix}
                    />
                  </div>
                </Accordion.Collapse>
              </div>
            );
          })}
        </div>
      </Accordion>
      <div
        className={
          "-mt-1px border-gray fw-bold text-primary mb-3 py-3 pl-3 d-block"
        }
        role={"button"}
        onClick={handleAdd}
      >
        + {addButtonText}
      </div>
    </div>
  );
};
